import React, { createContext, useState, useContext, useCallback, useEffect, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import {
  listMyChatSessions,
  getChatSessionDetails,
  updateChatSessionMetadata as apiUpdateChatSessionMetadata,
  deleteChatSession as apiDeleteChatSessionCall
} from '../services/chatSessionApi';
import { useAuth } from './AuthContext';
import { useSession } from './SessionContext';

export const ChatSessionContext = createContext(null);

export const useChatSessions = () => {
  const context = useContext(ChatSessionContext);
  if (!context) {
    throw new Error('useChatSessions must be used within a ChatSessionProvider');
  }
  return context;
};

const sortSessionsByActivity = (sessions) => {
  return (sessions || []).sort((a, b) => {
    return new Date(b.last_activity_at).getTime() - new Date(a.last_activity_at).getTime();
  });
};

const mapApiSessionToFrontend = (apiSession) => {
  if (!apiSession) return null;
  return {
    ...apiSession,
    save: !!apiSession.save_enabled,
  };
};

export const ChatSessionProvider = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const { sessionId: currentRuntimeSessionId, resetState: resetRuntimeSessionState } = useSession();
  const [chatSessions, setChatSessions] = useState([]);
  const [isLoadingChatSessions, setIsLoadingChatSessions] = useState(false);
  const [chatSessionsError, setChatSessionsError] = useState(null);
  const [activeChatSessionId, setActiveChatSessionId] = useState(null);
  const [detailedChatSession, setDetailedChatSession] = useState(null);
  const [isLoadingDetailedSession, setIsLoadingDetailedSession] = useState(false);
  const [detailedSessionError, setDetailedSessionError] = useState(null);

  // Refs for concurrency control and throttling
  const isFetchingRef = useRef(false);
  const lastFetchTimeRef = useRef(0);

  const loadChatSessions = useCallback(async (showLoading = true) => {
    if (!isAuthenticated) {
      setChatSessions([]);
      setChatSessionsError(null);
      return;
    }

    const now = Date.now();
    // Prevent concurrent requests AND throttle requests within 2 seconds
    // This stops the double-fetch on startup (Auth check + Session Init)
    if (isFetchingRef.current || (now - lastFetchTimeRef.current < 2000)) {
      return;
    }

    isFetchingRef.current = true;
    lastFetchTimeRef.current = now;

    if (showLoading) setIsLoadingChatSessions(true);
    setChatSessionsError(null);
    try {
      const sessionsFromApi = await listMyChatSessions();
      const mappedSessions = sessionsFromApi.map(mapApiSessionToFrontend);
      setChatSessions(sortSessionsByActivity(mappedSessions));
    } catch (err) {
      console.error("Error loading learning sessions:", err);
      setChatSessionsError(err.message || "Failed to load learning sessions.");
      setChatSessions([]);
    } finally {
      isFetchingRef.current = false;
      if (showLoading) setIsLoadingChatSessions(false);
    }
  }, [isAuthenticated]);

  const applyChatSessionUpdate = useCallback((updatedSessionData) => {
    setChatSessions(prevSessions => {
      const index = prevSessions.findIndex(s => s.id === updatedSessionData.id);
      if (index === -1) {
        console.log(`[ChatSessionContext] Adding new session ${updatedSessionData.id} from sync event.`);
        return sortSessionsByActivity([ ...prevSessions, mapApiSessionToFrontend(updatedSessionData) ]);
      }
      console.log(`[ChatSessionContext] Updating session ${updatedSessionData.id} from sync event.`);
      const newSessions = [...prevSessions];
      newSessions[index] = mapApiSessionToFrontend({ ...newSessions[index], ...updatedSessionData });
      return sortSessionsByActivity(newSessions);
    });
  }, []);

  const deleteChatSession = useCallback(async (sessionIdToDelete) => {
    if (!isAuthenticated) {
      throw new Error("User not authenticated.");
    }
    console.log(`[ChatSessionContext] Attempting to delete session: ${sessionIdToDelete}`);
    setChatSessionsError(null);
    try {
      await apiDeleteChatSessionCall(sessionIdToDelete);
      setChatSessions(prevSessions =>
        sortSessionsByActivity(prevSessions.filter(session => session.id !== sessionIdToDelete))
      );
      if (activeChatSessionId === sessionIdToDelete) {
        setActiveChatSessionId(null);
      }
      if (currentRuntimeSessionId === sessionIdToDelete) {
        if (resetRuntimeSessionState) {
            await resetRuntimeSessionState(true);
        }
      }
      return true;
    } catch (err) {
      console.error(`[ChatSessionContext] Error deleting session ${sessionIdToDelete}:`, err);
      setChatSessionsError(err.message || `Failed to delete session ${sessionIdToDelete}.`);
      throw err;
    }
  }, [
    isAuthenticated,
    activeChatSessionId,
    currentRuntimeSessionId,
    resetRuntimeSessionState,
  ]);

  useEffect(() => {
    if (isAuthenticated) {
      loadChatSessions();
    } else {
      setChatSessions([]);
      setChatSessionsError(null);
      setActiveChatSessionId(null);
      setDetailedChatSession(null);
      setDetailedSessionError(null);
    }
  }, [isAuthenticated, loadChatSessions]);

  useEffect(() => {
    if (activeChatSessionId && isAuthenticated) {
      const fetchDetails = async () => {
        setIsLoadingDetailedSession(true);
        setDetailedSessionError(null);
        setDetailedChatSession(null);
        try {
          const detailsFromApi = await getChatSessionDetails(activeChatSessionId);
          const mappedDetails = mapApiSessionToFrontend(detailsFromApi);
          setDetailedChatSession(mappedDetails);
        } catch (err) {
          console.error(`[ChatSessionContext] Error loading details for session ${activeChatSessionId}:`, err);
          if (err.status === 404) {
            deleteChatSession(activeChatSessionId);
          } else {
            setDetailedSessionError(err.message || "Failed to load session details.");
          }
        } finally {
          setIsLoadingDetailedSession(false);
        }
      };
      fetchDetails();
    } else if (!activeChatSessionId) {
        setDetailedChatSession(null);
        setDetailedSessionError(null);
    }
  }, [activeChatSessionId, isAuthenticated, deleteChatSession]);

  const updateSessionMetadata = useCallback(async (sessionId, updateData) => {
    if (!isAuthenticated) {
      throw new Error("User not authenticated.");
    }
    try {
      const updatedSessionFromApi = await apiUpdateChatSessionMetadata(sessionId, updateData);
      const mappedUpdatedSession = mapApiSessionToFrontend(updatedSessionFromApi);
      setChatSessions(prevSessions =>
        sortSessionsByActivity(
          prevSessions.map(session =>
            session.id === sessionId ? { ...session, ...mappedUpdatedSession } : session
          )
        )
      );
      if (detailedChatSession && detailedChatSession.id === sessionId) {
        setDetailedChatSession(prev => ({ ...prev, ...mappedUpdatedSession }));
      }
      return mappedUpdatedSession;
    } catch (err) {
      console.error("Error updating session metadata:", err);
      throw err;
    }
  }, [isAuthenticated, detailedChatSession]);

  const contextValue = useMemo(() => ({
    chatSessions,
    isLoadingChatSessions,
    chatSessionsError,
    loadChatSessions,
    activeChatSessionId,
    setActiveChatSessionId,
    detailedChatSession,
    isLoadingDetailedSession,
    detailedSessionError,
    updateSessionMetadata,
    deleteChatSession,
    applyChatSessionUpdate,
  }), [
    chatSessions,
    isLoadingChatSessions,
    chatSessionsError,
    loadChatSessions,
    activeChatSessionId,
    detailedChatSession,
    isLoadingDetailedSession,
    detailedSessionError,
    updateSessionMetadata,
    deleteChatSession,
    applyChatSessionUpdate,
  ]);

  return (
    <ChatSessionContext.Provider value={contextValue}>
      {children}
    </ChatSessionContext.Provider>
  );
};

ChatSessionProvider.propTypes = {
  children: PropTypes.node.isRequired,
};